import { TracksTypes } from '../../Interface/track'
import { TrackResolverAbstract } from './TrackResolverAbstract'
import { PlayerLogicTrackType, RepeatMode } from './Types'

type PlaylistTrackType = {
  track: PlayerLogicTrackType<any> | null
  trackId: string | number
  type: TracksTypes
}

export class PlaylistManager {
  private playlist: Array<PlaylistTrackType> = []

  private repeatMode: RepeatMode = 'off'
  private isShuffleActive = false
  private shuffledTracks: PlayerLogicTrackType<any>[] = []
  private playedInShuffle: (string | number)[] = []

  constructor(public tracksAPI: TrackResolverAbstract) {}

  /** PUBLIC METHODS */

  public setPlaylist(tracks: PlayerLogicTrackType<any>[], type: TracksTypes = 'beat'): void {
    this.playlist = tracks.map(t => ({
      track: t,
      type,
      trackId: t.id
    }))

    if (this.isShuffleActive) {
      this.updateShuffleWithNewTracks()
    }
  }

  public setPlaylistTracksIds(tracksIds: Array<string | number>, type: TracksTypes = 'beat'): void {
    this.playlist = tracksIds.map(id => ({
      track: null,
      type,
      trackId: id
    }))
  }

  public setRepeatMode(mode: RepeatMode): void {
    this.repeatMode = mode
  }

  public setShuffleMode(newShuffleState: boolean, currentTrack?: PlayerLogicTrackType<any>): void {
    this.isShuffleActive = newShuffleState
    if (newShuffleState) {
      this.initShuffle(currentTrack)
    }
  }

  public async getNextTrack(currentTrack: PlayerLogicTrackType<any>): Promise<PlayerLogicTrackType<any> | null> {
    return this.isShuffleActive
      ? this.getShuffleTrack(currentTrack, 'next')
      : this.getOrderedTrack(currentTrack, 'next')
  }

  public async getPrevTrack(currentTrack: PlayerLogicTrackType<any>): Promise<PlayerLogicTrackType<any> | null> {
    return this.isShuffleActive
      ? this.getShuffleTrack(currentTrack, 'prev')
      : this.getOrderedTrack(currentTrack, 'prev')
  }

  /** PRIVATE METHODS */

  private async updateShuffleWithNewTracks(): Promise<void> {
    const allTracks = await this.getAllPreparedTracks()

    const shuffledTrackIds = new Set(this.shuffledTracks.map(t => t.id))
    const newTracks = allTracks.filter(track => !shuffledTrackIds.has(track.id))

    if (newTracks.length > 0) {
      this.shuffledTracks.push(...this.shuffleTracks(newTracks))
    }
  }

  private async initShuffle(currentTrack?: PlayerLogicTrackType<any>): Promise<void> {
    const allTracks = await this.getAllPreparedTracks()
    this.shuffledTracks = this.shuffleTracks(allTracks)
    this.playedInShuffle = currentTrack?.id ? [currentTrack.id] : []
  }

  private async getAllPreparedTracks(): Promise<PlayerLogicTrackType<any>[]> {
    const preparedTracks = await Promise.all(this.playlist.map(item => this.prepareAndReturnTrack(item)))
    return preparedTracks.filter((track): track is PlayerLogicTrackType<any> => Boolean(track))
  }

  private async getShuffleTrack(
    currentTrack: PlayerLogicTrackType<any>,
    direction: 'next' | 'prev'
  ): Promise<PlayerLogicTrackType<any> | null> {
    return direction === 'next' ? this.getNextShuffleTrack() : this.getPrevShuffleTrack(currentTrack)
  }

  private async getNextShuffleTrack(): Promise<PlayerLogicTrackType<any> | null> {
    const unplayed = this.shuffledTracks.filter(t => !this.playedInShuffle.includes(t.id))

    if (unplayed.length > 0) {
      const nextTrack = unplayed[0]
      this.playedInShuffle.push(nextTrack.id)
      return nextTrack
    }

    if (this.repeatMode === 'all') {
      this.shuffledTracks = this.shuffleTracks(this.shuffledTracks)
      this.playedInShuffle = []
      const firstTrack = this.shuffledTracks[0] || null
      if (firstTrack) {
        this.playedInShuffle.push(firstTrack.id)
      }
      return firstTrack
    }

    return null
  }

  private async getPrevShuffleTrack(
    currentTrack: PlayerLogicTrackType<any>
  ): Promise<PlayerLogicTrackType<any> | null> {
    const currentIndex = this.shuffledTracks.findIndex(t => t.id === currentTrack.id)

    if (currentIndex <= 0) {
      return this.repeatMode === 'all' ? this.shuffledTracks[this.shuffledTracks.length - 1] || null : null
    }

    return this.shuffledTracks[currentIndex - 1]
  }

  private async getOrderedTrack(
    currentTrack: PlayerLogicTrackType<any>,
    direction: 'next' | 'prev'
  ): Promise<PlayerLogicTrackType<any> | null> {
    return direction === 'next' ? this.getNextOrderedTrack(currentTrack) : this.getPrevOrderedTrack(currentTrack)
  }

  private async getNextOrderedTrack(
    currentTrack: PlayerLogicTrackType<any>
  ): Promise<PlayerLogicTrackType<any> | null> {
    const index = this.playlist.findIndex(item => item.trackId === currentTrack.id)
    if (index === -1) {
      return null
    }

    if (index + 1 < this.playlist.length) {
      return this.prepareAndReturnTrack(this.playlist[index + 1])
    }

    return this.repeatMode === 'all' ? this.prepareAndReturnTrack(this.playlist[0]) : null
  }

  private async getPrevOrderedTrack(
    currentTrack: PlayerLogicTrackType<any>
  ): Promise<PlayerLogicTrackType<any> | null> {
    const index = this.playlist.findIndex(item => item.trackId === currentTrack.id)
    if (index === -1) {
      return null
    }

    if (index - 1 >= 0) {
      return this.prepareAndReturnTrack(this.playlist[index - 1])
    }

    return this.repeatMode === 'all' ? this.prepareAndReturnTrack(this.playlist[this.playlist.length - 1]) : null
  }

  private shuffleTracks(tracks: PlayerLogicTrackType<any>[]): PlayerLogicTrackType<any>[] {
    const shuffled = [...tracks]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  private async prepareAndReturnTrack(track: PlaylistTrackType): Promise<PlayerLogicTrackType<any>> {
    if (track.track == null) {
      const resultTrack = await this.tracksAPI.getTrack(track.trackId, track.type)
      // cache result for future;
      track.track = resultTrack
    }
    return track.track
  }
}
